import { useCallback, useEffect, useState } from 'react';
import ReactHtmlParser from 'react-html-parser';

import http from 'services/http';

/**
 * Central de ajuda no painel da banca.
 *
 * Só leitura: o conteúdo é escrito pela Setebit na Central e chega aqui pela API,
 * já filtrado pelo nível do usuário logado — o cambista não recebe tutorial de
 * descarga nem de configuração de impressora.
 *
 * A lista vem sem o corpo dos tutoriais e o texto de cada tópico só é buscado
 * quando o usuário abre. O que já foi aberto fica em memória, então reabrir não
 * refaz a chamada.
 */
const ESPERA_BUSCA_MS = 350;

const SEM_CATEGORIA = 'outros';

export default function Suporte() {
	const [termo, setTermo] = useState('');
	const [busca, setBusca] = useState('');
	const [categorias, setCategorias] = useState([]);
	const [topicos, setTopicos] = useState([]);
	const [loading, setLoading] = useState(true);
	const [erro, setErro] = useState(false);
	const [aberto, setAberto] = useState(null);
	const [detalhes, setDetalhes] = useState({});

	// A busca vai ao servidor porque alcança também o texto de dentro do tutorial
	// — quem não lembra o nome da tela procura pela palavra que viu nela. O atraso
	// evita uma requisição por tecla digitada.
	useEffect(() => {
		const espera = setTimeout(() => setBusca(termo.trim()), ESPERA_BUSCA_MS);

		return () => clearTimeout(espera);
	}, [termo]);

	const carregar = useCallback(async () => {
		setLoading(true);

		try {
			const { data: { data } } = await http.get('ajuda', {
				params: { q: busca || undefined },
			});

			setCategorias(data?.categorias ?? []);
			setTopicos(data?.topicos ?? []);
			setErro(false);
		} catch (e) {
			setCategorias([]);
			setTopicos([]);
			setErro(true);
		} finally {
			setLoading(false);
		}
	}, [busca]);

	useEffect(() => {
		carregar();
	}, [carregar]);

	const abrir = async (topico) => {
		if (aberto === topico.id) {
			setAberto(null);
			return;
		}

		setAberto(topico.id);

		// Só o que carregou de verdade é reaproveitado: um tópico que falhou volta
		// a undefined e é buscado de novo quando o usuário reabre.
		if (detalhes[topico.id]) return;

		setDetalhes((atuais) => ({ ...atuais, [topico.id]: undefined }));

		try {
			const { data: { data } } = await http.get(`ajuda/${topico.id}`);
			setDetalhes((atuais) => ({ ...atuais, [topico.id]: data }));
		} catch (e) {
			// null marca "tentou e não veio": a tela mostra o recado de erro em vez
			// de ficar carregando para sempre.
			setDetalhes((atuais) => ({ ...atuais, [topico.id]: null }));
		}
	};

	// Agrupa na ordem das categorias e joga em "Outros" o que não tem categoria.
	// Grupo sem tópico não aparece — na busca, isso deixa só o que casou.
	const grupos = [
		...categorias.map((categoria) => ({
			chave: categoria.id,
			nome: categoria.nome,
			topicos: topicos.filter((topico) => topico.categoria_id === categoria.id),
		})),
		{
			chave: SEM_CATEGORIA,
			nome: 'Outros',
			topicos: topicos.filter((topico) => (
				!categorias.some((categoria) => categoria.id === topico.categoria_id)
			)),
		},
	].filter((grupo) => grupo.topicos.length > 0);

	return (
		<div className="content">
			<div className="card">
				<div className="card-header">
					<h4 className="card-title" style={styles.titulo}>Suporte</h4>
					<p style={styles.ajuda}>
						Tutoriais sobre como usar o sistema. Clique no tópico para ler o passo a passo.
					</p>
				</div>

				<div className="card-body">
					<input
						type="search"
						className="form-control"
						style={styles.busca}
						placeholder="Buscar por assunto (ex.: descarga, impressora, resultado)"
						value={termo}
						onChange={(e) => setTermo(e.target.value)}
					/>

					{loading ? (
						<p style={styles.vazio}>Carregando tutoriais...</p>
					) : erro ? (
						<p style={styles.vazio}>
							Não foi possível carregar os tutoriais agora. Tente novamente em instantes.
						</p>
					) : grupos.length === 0 ? (
						<p style={styles.vazio}>
							{busca
								? `Nenhum tutorial encontrado para "${busca}".`
								: 'Nenhum tutorial disponível por aqui ainda.'}
						</p>
					) : (
						grupos.map((grupo) => (
							<div key={grupo.chave} style={styles.grupo}>
								<h6 style={styles.grupoTitulo}>{grupo.nome}</h6>

								<ul style={styles.lista}>
									{grupo.topicos.map((topico) => {
										const detalhe = detalhes[topico.id];
										const estaAberto = aberto === topico.id;

										return (
											<li key={topico.id} style={styles.item}>
												<div style={styles.itemTopo} onClick={() => abrir(topico)}>
													<div>
														<span style={styles.itemTitulo}>{topico.titulo}</span>
														{topico.tem_video && <span style={styles.tagVideo}>vídeo</span>}
													</div>
													<i
														className={`fa fa-chevron-${estaAberto ? 'up' : 'down'}`}
														style={styles.seta}
													/>
												</div>

												{!estaAberto && topico.resumo && (
													<p style={styles.resumo} onClick={() => abrir(topico)}>
														{topico.resumo}
													</p>
												)}

												{estaAberto && (
													<div style={styles.corpo}>
														{detalhe === undefined ? (
															<p style={styles.vazio}>Carregando...</p>
														) : detalhe === null ? (
															<p style={styles.vazio}>
																Não foi possível abrir este tutorial. Feche e abra de
																novo para tentar outra vez.
															</p>
														) : (
															<>
																{/* HTML saneado na gravação, na API — ver SanitizaHtmlAjuda. */}
																<div style={styles.texto}>
																	{ReactHtmlParser(detalhe.conteudo)}
																</div>

																{detalhe.video_url && (
																	<a
																		href={detalhe.video_url}
																		target="_blank"
																		rel="noopener noreferrer"
																		className="btn btn-sm btn-primary"
																		style={styles.botaoVideo}
																	>
																		<i className="fa fa-play-circle" style={{ marginRight: 6 }} />
																		{detalhe.video_rotulo || 'Assistir ao vídeo'}
																	</a>
																)}
															</>
														)}
													</div>
												)}
											</li>
										);
									})}
								</ul>
							</div>
						))
					)}
				</div>
			</div>
		</div>
	);
}

const styles = {
	titulo: { marginBottom: 4 },
	ajuda: { margin: 0, fontSize: 13, color: '#667085' },
	busca: { maxWidth: 460, marginBottom: 18 },
	grupo: { marginBottom: 22 },
	grupoTitulo: {
		fontSize: 12, letterSpacing: '.06em', textTransform: 'uppercase',
		color: '#98a2b3', fontWeight: 600, marginBottom: 8,
	},
	lista: { listStyle: 'none', margin: 0, padding: 0 },
	item: {
		background: '#fff', border: '1px solid #e4e6eb', borderRadius: 8,
		padding: '12px 16px', marginBottom: 10,
	},
	itemTopo: {
		display: 'flex', justifyContent: 'space-between', alignItems: 'center',
		gap: 10, cursor: 'pointer', flexWrap: 'wrap',
	},
	itemTitulo: { fontSize: 15, color: '#1f2328', fontWeight: 600 },
	tagVideo: {
		background: '#fce8ea', color: '#b32233', borderRadius: 4, marginLeft: 8,
		fontSize: 10, padding: '2px 6px', textTransform: 'uppercase',
	},
	seta: { color: '#98a2b3', fontSize: 12 },
	resumo: {
		display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
		overflow: 'hidden', fontSize: 13, color: '#667085', margin: '6px 0 0', cursor: 'pointer',
	},
	corpo: { borderTop: '1px solid #f1f3f5', marginTop: 10, paddingTop: 12 },
	texto: { fontSize: 14, lineHeight: 1.6, color: '#344054' },
	botaoVideo: { marginTop: 14 },
	vazio: { fontSize: 14, color: '#667085', margin: 0 },
};
